import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEnhancedAuth } from '@/contexts/AuthContext';
import { useUserData } from '@/contexts/AuthContext';
import LoadingSpinner from '@/shared/components/LoadingSpinner';
import { logger } from '@/utils/logger';
import { safeReturnPath } from '@/utils/redirectSecurity';

/**
 * Brief hand-off screen after sign-in / trial creation. It ALWAYS fails forward —
 * a signed-in user is never trapped here. It proceeds the instant the user doc lands,
 * the moment the data context goes idle (even with no doc — Dashboard handles that),
 * or after a hard 12s ceiling. (Previously: a broken 30s "timeout" with no real timer
 * + a one-shot data fetch that could leave the spinner up forever.)
 */
const AccountSetupPage: React.FC = () => {
  const { currentUser } = useEnhancedAuth();
  const { userData, isLoadingUserData } = useUserData();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const isTrialSetup = searchParams.get('type') === 'trial' || !userData?.profile?.onboardingCompleted;
  const redirectPath = safeReturnPath(searchParams.get('redirect'));

  useEffect(() => {
    if (!currentUser) {
      navigate('/signin', { replace: true });
      return;
    }
    // User doc is ready → straight into the product (onboarding is a soft Dashboard modal).
    if (userData) {
      logger.info('AccountSetupPage', 'User doc ready — routing to product', { redirectPath });
      navigate(redirectPath, { replace: true });
      return;
    }
    // FAIL FORWARD: useUserDataState has finished its retries but still has no doc
    // (e.g. createTrialUser is slow). Don't trap the user — go into the app; the Dashboard
    // handles a missing profile (defaults + soft onboarding + its own refetch).
    if (!isLoadingUserData) {
      logger.warn('AccountSetupPage', 'Data context idle without a doc — failing forward', { redirectPath });
      navigate(redirectPath, { replace: true });
    }
  }, [currentUser, userData, isLoadingUserData, navigate, redirectPath]);

  // Hard ceiling: a signed-in user is never trapped on this screen. Proceed after 12s no matter what.
  useEffect(() => {
    if (!currentUser) return;
    const t = setTimeout(() => {
      logger.warn('AccountSetupPage', 'Setup ceiling reached — failing forward into the app', { redirectPath });
      navigate(redirectPath, { replace: true });
    }, 12000);
    return () => clearTimeout(t);
  }, [currentUser, navigate, redirectPath]);

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-neutral-100 rounded-2xl shadow-ledge-lg p-8 text-center">
        <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <LoadingSpinner className="text-primary-600" />
        </div>
        <h2 className="text-2xl font-bold text-neutral-900 mb-4">
          {isTrialSetup ? 'Setting Up Your Account' : 'Preparing Your Dashboard'}
        </h2>
        <p className="text-neutral-600">
          {isTrialSetup
            ? "We're preparing your personalized study dashboard. This will just take a moment."
            : "We're loading your account. This will just take a moment."}
        </p>
      </div>
    </div>
  );
};

export default AccountSetupPage;
